import _classnames from 'clsx';
import PropTypes from 'prop-types';
import React, { forwardRef } from 'react';
import Icon from '../../icons/Icon/Icon';

/**
 * A pure class component that renders a select input with a label. Accepts data in the form of
 * either an array of strings or an array of objects.
 */
class Select extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      isFocused: false,
    };
  }

  handleBlur = () => {
    this.setState({ isFocused: false });
  };

  handleFocus = () => {
    this.setState({ isFocused: true });
  };

  static renderOptions = (options) => {
    if (typeof options[0] === 'string') {
      return options.map((option) => (
        <option key={`option-${option}`} value={option}>
          {option}
        </option>
      ));
    }
    return Object.entries(options).map(([index, option]) => (
      <option
        key={`option-${index}-${option.value}`}
        value={option.value}
        disabled={option.disabled}
      >
        {option.name}
      </option>
    ));
  };

  render() {
    const {
      classes,
      disabled,
      innerRef,
      onChange,
      optGroups,
      options,
      placeholder,
      status,
      value,
    } = this.props;
    const { isFocused } = this.state;

    const placeholderElement = placeholder ? (
      <option value="" hidden>
        {placeholder}
      </option>
    ) : null;
    let displayValue;
    if (value === '') {
      displayValue = placeholder;
    } else {
      const allOptions = optGroups ? optGroups.flatMap((group) => group.options) : options;
      if (typeof allOptions[0] === 'string') {
        displayValue = value;
      } else {
        const selectedOption = allOptions.find((option) => option.value === value);
        displayValue = selectedOption.name;
      }
    }
    return (
      <div
        className={_classnames('Select', classes, {
          'Select--disabled': disabled,
          'Select--focused': isFocused,
          [`Select--${status}`]: !!status,
        })}
      >
        <div className="Select__overlay">
          <div className="Select__overlay__value">{displayValue}</div>
          <Icon
            classes="Select__overlay__arrow"
            name="Chevron--single--south"
            size="minor"
            svgProps={{ style: { fill: disabled ? '#969fb1' : '#545d79' } }}
          />
        </div>
        <select
          className="Select__input"
          disabled={disabled}
          onBlur={this.handleBlur}
          onChange={onChange}
          onFocus={this.handleFocus}
          ref={innerRef}
          value={value === '' ? placeholder : value}
        >
          {placeholderElement}
          {optGroups
            ? optGroups.map((group) => (
                <optgroup key={`optgroup-${group.label}`} label={group.label}>
                  {Select.renderOptions(group.options)}
                </optgroup>
              ))
            : Select.renderOptions(options)}
        </select>
      </div>
    );
  }
}

export const optionproptypes = PropTypes.oneOfType([
  PropTypes.arrayOf(PropTypes.string),
  PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      displayValue: PropTypes.string.isRequired,
    })
  ),
]);

Select.propTypes = {
  /**
   * A space-separated list of classes to apply to the outer `div` wrappping the component
   */
  classes: PropTypes.string,
  /**
   * A function to run when the select element's value is changed
   */
  onChange: PropTypes.func.isRequired,
  /**
   * The options available inside the select element; can be either an array of strings or an array
   * of objects
   */
  options: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.string),
    PropTypes.arrayOf(
      PropTypes.shape({
        value: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        displayValue: PropTypes.string.isRequired,
      })
    ),
  ]),
  /**
   * If the options within the select element should be grouped, this prop should be used rather
   * than the options prop. The objects in the array for this prop should include a `label` key for
   * each group and an `options` array in the same expected format as the `options` prop.
   */
  optGroups: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      options: optionproptypes,
    })
  ),
  /**
   * The placeholder text shown inside the select component before a selection has been made
   */
  placeholder: PropTypes.string,
  /**
   * Adds a class to the `div` wrapping the component (`.Select--yourStatusStringHere`).
   * Current styles exist for these statuses: `success`, `warning`
   */
  status: PropTypes.string,
  disabled: PropTypes.bool,
  /**
   * Inner Ref
   */
  innerRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]),
  /**
   * Current input value
   */
  value: PropTypes.string,
};

Select.defaultProps = {
  value: '',
  classes: undefined,
  options: undefined,
  optGroups: undefined,
  placeholder: undefined,
  status: undefined,
  disabled: false,
  innerRef: undefined,
};

export default forwardRef((props, ref) => <Select innerRef={ref} {...props} />);
